import React, { createContext, useContext, useState, useCallback, useRef } from 'react';
import { parseServerDate, formatDate } from '../utils/helpers';

// Notification types
export const NOTIFICATION_TYPES = {
    FUNDING_REQUEST: 'funding_request',
    FUNDING_APPROVED: 'funding_approved',
    FUNDING_REJECTED: 'funding_rejected',
    PROJECT_CREATED: 'project_created',
    PROJECT_STATUS: 'project_status',
    TASK_ASSIGNED: 'task_assigned',
    TASK_OVERDUE: 'task_overdue',
    BUDGET_WARNING: 'budget_warning',
    BUDGET_exceed: 'budget_exceed',
    CONSULTANT_added: 'consultant_added',
    RECOMMENDATION: 'recommendation',
    COMMENT: 'comment',
    SYSTEM: 'system'
};

// Priority levels
export const PRIORITY = {
    LOW: 'low',
    MEDIUM: 'medium',
    HIGH: 'high',
    CRITICAL: 'critical'
};

// Helper to format relative time
export const formatRelativeTime = (date) => {
    // Normalise the server timestamp (naive UTC, possibly with microseconds) so it
    // never yields "Invalid Date".
    const dateObj = parseServerDate(date)
    if (!dateObj) return ''
    const now = new Date()
    const diffMs = now - dateObj

    // If future date (backend clock skew), say "Just now"
    if (diffMs < 0) return 'Just now';

    const diffMins = Math.floor(diffMs / (1000 * 60))
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60))
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24))

    if (diffMins < 1) return 'Just now'
    if (diffMins < 60) return `${diffMins}m ago`
    if (diffHours < 24) return `${diffHours}h ago`
    if (diffDays < 7) return `${diffDays}d ago`
    return formatDate(dateObj)
}


// Mock notifications data
const initialNotifications = [
    {
        id: 1,
        type: NOTIFICATION_TYPES.FUNDING_REQUEST,
        title: 'New Funding Request',
        message: 'Mike Johnson submitted a $320,000 funding request for Mobile App development',
        priority: PRIORITY.HIGH,
        read: false,
        timestamp: new Date(Date.now() - 5 * 60 * 1000).toISOString(), // 5 mins ago
        link: '/funding/approval',
        actionRequired: true,
        data: { projectId: 2, requestId: 1, amount: 320000 }
    },
    {
        id: 2,
        type: NOTIFICATION_TYPES.BUDGET_WARNING,
        title: 'Budget Alert: 80% Threshold',
        message: 'E-commerce Platform has reached 80% of allocated budget',
        priority: PRIORITY.HIGH,
        read: false,
        timestamp: new Date(Date.now() - 30 * 60 * 1000).toISOString(), // 30 mins ago
        link: '/finance/budget',
        actionRequired: false,
        data: { projectId: 1, percentage: 80 }
    },
    {
        id: 3,
        type: NOTIFICATION_TYPES.TASK_ASSIGNED,
        title: 'New Task Assigned',
        message: 'You have been assigned "Payment Gateway Integration" task',
        priority: PRIORITY.MEDIUM,
        read: false,
        timestamp: new Date(Date.now() - 2 * 60 * 60 * 1000).toISOString(), // 2 hours ago
        link: '/projects/1',
        actionRequired: true,
        data: { projectId: 1, taskId: 4 }
    },
    {
        id: 4,
        type: NOTIFICATION_TYPES.FUNDING_APPROVED,
        title: 'Funding Approved',
        message: 'Your funding request for Data Analytics Dashboard has been approved',
        priority: PRIORITY.MEDIUM,
        read: true,
        timestamp: new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString(), // 1 day ago
        link: '/projects/3',
        actionRequired: false,
        data: { projectId: 3, requestId: 3, amount: 45000 }
    },
    {
        id: 5,
        type: NOTIFICATION_TYPES.RECOMMENDATION,
        title: 'New Consultant Recommendation',
        message: 'Abhi Consultant submitted a recommendation for E-commerce Platform',
        priority: PRIORITY.LOW,
        read: true,
        timestamp: new Date(Date.now() - 2 * 24 * 60 * 60 * 1000).toISOString(), // 2 days ago
        link: '/consultant/recommendations',
        actionRequired: false,
        data: { projectId: 1, recommendationId: 1 }
    },
    {
        id: 6,
        type: NOTIFICATION_TYPES.PROJECT_STATUS,
        title: 'Project Status Update',
        message: 'Cloud Infrastructure Migration marked as Completed',
        priority: PRIORITY.LOW,
        read: true,
        timestamp: new Date(Date.now() - 3 * 24 * 60 * 60 * 1000).toISOString(), // 3 days ago
        link: '/projects/4',
        actionRequired: false,
        data: { projectId: 4, oldStatus: 'In Progress', newStatus: 'Completed' }
    }
];

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:8000';
const getAuthheader = () => {
  const user = JSON.parse(localStorage.getItem('user'));
  return user?.access_token ? { Authorization: `Bearer ${user.access_token}` } : {};
};

const NotificationContext = createContext(null);

import api from '../services/api';
import { useEffect } from 'react';

export function NotificationProvider({ children }) {
    const [notifications, setNotifications] = useState([]);
    const [isOpen, setIsOpen] = useState(false);
    const [loading, setLoading] = useState(false);

    const lastUser = useRef(null);
    const fetchNotifications = useCallback(async () => {
        // Whose list this is. When somebody else signs in without a page reload, the previous
        // person's notifications are still in memory; drop them before anything is shown.
        let who = null;
        try { who = JSON.parse(localStorage.getItem('user'))?.access_token || null; } catch { who = null; }
        if (lastUser.current !== null && lastUser.current !== who) setNotifications([]);
        lastUser.current = who;
        setLoading(true);
        try {
            const response = await api.get('notifications/');
            // The API serialises the time as `created_at`; the UI reads `timestamp`.
            // Map it so the relative time renders instead of "Invalid Date".
            setNotifications((response.data || []).map(n => ({
                ...n,
                timestamp: n.timestamp || n.created_at,
            })));
        } catch (e) {
            console.error("Failed to fetch notifications", e);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        const token = JSON.parse(localStorage.getItem('user'))?.access_token;
        if (!token) return;
        fetchNotifications();
        // Poll on a short interval so the bell badge updates on its own (was 60s — too slow,
        // so a new notification appeared to only show once the dropdown was opened).
        const interval = setInterval(fetchNotifications, 10000);
        // And refresh the INSTANT the user returns to the tab / app window, so switching
        // back (e.g. after a PM approves in another window) shows the badge immediately —
        // never waiting for the next poll tick.
        const onVisible = () => { if (document.visibilityState === 'visible') fetchNotifications(); };
        document.addEventListener('visibilitychange', onVisible);
        window.addEventListener('focus', fetchNotifications);
        return () => {
            clearInterval(interval);
            document.removeEventListener('visibilitychange', onVisible);
            window.removeEventListener('focus', fetchNotifications);
        };
    }, [fetchNotifications]);

    const unreadCount = Array.isArray(notifications) ? notifications.filter(n => !n.read).length : 0;

    const markAsRead = useCallback(async (id) => {
        try {
            await api.put(`notifications/${id}/read/`, {});
            setNotifications(prev =>
                prev.map(n => n.id === id ? { ...n, read: true } : n)
            );
        } catch (e) {
            console.error("Failed to mark notification as read", e);
        }
    }, []);

    const markAllAsRead = useCallback(async () => {
        try {
            const response = await api.put('notifications/read-all/');
            setNotifications(response.data);
        } catch (e) {
            console.error("Failed to mark all as read", e);
        }
    }, []);

    // Add new notification (Client side mock/emit)
    const addNotification = useCallback((notification) => {
        const newNotification = {
            ...notification,
            id: Date.now(),
            timestamp: new Date().toISOString(),
            read: false
        };
        setNotifications(prev => [newNotification, ...prev]);
    }, []);

    // Remove notification
    const removeNotification = useCallback((id) => {
        setNotifications(prev => prev.filter(n => n.id !== id));
    }, []);

    // Clear all notifications
    const clearAll = useCallback(() => {
        setNotifications([]);
    }, []);

    // Toggle dropdown — also refresh on open so the list is always current when clicked.
    const toggleDropdown = useCallback(() => {
        setIsOpen(prev => {
            const next = !prev;
            if (next) fetchNotifications();
            return next;
        });
    }, [fetchNotifications]);

    const closeDropdown = useCallback(() => {
        setIsOpen(false);
    }, []);

    const value = {
        notifications,
        unreadCount,
        isOpen,
        markAsRead,
        markAllAsRead,
        addNotification,
        removeNotification,
        clearAll,
        toggleDropdown,
        closeDropdown
    };

    return (
        <NotificationContext.Provider value={value}>
            {children}
        </NotificationContext.Provider>
    );
}

export function useNotifications() {
    const context = useContext(NotificationContext);
    if (!context) {
        throw new Error('useNotifications must be used within a NotificationProvider');
    }
    return context;
}

export default NotificationContext;
